import React, { useEffect, useState } from 'react';
import client from '../../lib/feathers';

/**
 * Card component for managing per-varietal drinking-window aging overrides on
 * an appellation. Lists existing entries plus an add form. Each row supports
 * inline edit + delete. agingStart/agingEnd are years offset from vintage year.
 */
export default function VarietalAgingOverrides({ appellationId, appellationName }) {
  const [varietals, setVarietals] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add-form state
  const [newVarietalId, setNewVarietalId] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);

  // Per-row edit state — keyed by entry id
  const [editing, setEditing] = useState({});

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const ovService = client.service('admin/appellation-varietal-aging');
      const ovResult = await ovService.find({
        query: { appellationId, $limit: 200 },
      });
      setEntries(ovResult.data);
      // Varietals: load once if not already loaded
      if (varietals.length === 0) {
        // Offered names only, and all of them: since the grape seed the table
        // holds about 700 rows (443 hidden historic plate names), and a
        // $limit of 200 sorted by name stopped partway through the alphabet.
        const varietalSvc = client.service('admin/varietal');
        const varietalResult = await varietalSvc.find({
          query: { offerInPicker: true, $sort: { name: 1 }, $limit: 1000 },
        });
        setVarietals(varietalResult.data || varietalResult);
      }
    } catch (e) {
      console.error('Error loading aging overrides:', e);
      setError('Failed to load aging overrides.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appellationId) loadAll();
    // eslint-disable-next-line
  }, [appellationId]);

  const handleAdd = async () => {
    if (!newVarietalId) return;
    try {
      setAdding(true);
      const svc = client.service('admin/appellation-varietal-aging');
      await svc.create({
        appellationId,
        varietalId: newVarietalId,
        agingStart: newStart === '' ? null : parseInt(newStart, 10),
        agingEnd: newEnd === '' ? null : parseInt(newEnd, 10),
        notes: newNotes || null,
      });
      setNewVarietalId('');
      setNewStart('');
      setNewEnd('');
      setNewNotes('');
      await loadAll();
    } catch (e) {
      console.error(e);
      window.alert(`Failed to add override: ${e.message}`);
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (entry) => {
    setEditing(prev => ({
      ...prev,
      [entry.id]: {
        agingStart: entry.agingStart == null ? '' : String(entry.agingStart),
        agingEnd: entry.agingEnd == null ? '' : String(entry.agingEnd),
        notes: entry.notes || '',
      },
    }));
  };

  const cancelEdit = (entryId) => {
    setEditing(prev => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
  };

  const saveEdit = async (entryId) => {
    const e = editing[entryId];
    try {
      const svc = client.service('admin/appellation-varietal-aging');
      await svc.patch(entryId, {
        agingStart: e.agingStart === '' ? null : parseInt(e.agingStart, 10),
        agingEnd: e.agingEnd === '' ? null : parseInt(e.agingEnd, 10),
        notes: e.notes || null,
      });
      cancelEdit(entryId);
      await loadAll();
    } catch (err) {
      console.error(err);
      window.alert(`Failed to save: ${err.message}`);
    }
  };

  const handleDelete = async (entry) => {
    const v = entry.varietal && entry.varietal.name;
    if (!window.confirm(`Remove the aging override for ${v || 'this varietal'} in ${appellationName}?`)) return;
    try {
      const svc = client.service('admin/appellation-varietal-aging');
      await svc.remove(entry.id);
      await loadAll();
    } catch (err) {
      console.error(err);
      window.alert(`Failed to delete: ${err.message}`);
    }
  };

  // Filter out varietals that already have an override on this appellation
  const usedVarietalIds = new Set(entries.map(e => e.varietalId));
  const availableVarietals = varietals.filter(v => !usedVarietalIds.has(v.id));

  return (
    <div className="mt-6 bg-white shadow sm:rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Varietal Aging Overrides</h3>
          <p className="text-sm text-gray-500 mt-1">
            Per-varietal drinking-window years for wines from this appellation. Overrides the global varietal default. Years are relative to vintage year.
          </p>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && entries.length === 0 && (
        <p className="text-sm text-gray-500">No varietal overrides yet.</p>
      )}

      {entries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Varietal</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Aging Start (yrs)</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Aging End (yrs)</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.map(entry => {
                const isEditing = !!editing[entry.id];
                const eState = editing[entry.id] || {};
                return (
                  <tr key={entry.id}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {entry.varietal ? entry.varietal.name : entry.varietalId}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          value={eState.agingStart}
                          onChange={ev => setEditing(p => ({ ...p, [entry.id]: { ...p[entry.id], agingStart: ev.target.value } }))}
                          className="w-20 border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (entry.agingStart ?? '—')}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          value={eState.agingEnd}
                          onChange={ev => setEditing(p => ({ ...p, [entry.id]: { ...p[entry.id], agingEnd: ev.target.value } }))}
                          className="w-20 border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (entry.agingEnd ?? '—')}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500 max-w-md">
                      {isEditing ? (
                        <input
                          type="text"
                          value={eState.notes}
                          onChange={ev => setEditing(p => ({ ...p, [entry.id]: { ...p[entry.id], notes: ev.target.value } }))}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (entry.notes || '')}
                    </td>
                    <td className="px-3 py-2 text-sm text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => saveEdit(entry.id)}
                            className="text-primary hover:text-red-900 mr-3"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelEdit(entry.id)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(entry)}
                            className="text-primary hover:text-red-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(entry)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add form */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Add Override</h4>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={newVarietalId}
            onChange={(e) => setNewVarietalId(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="">Select varietal…</option>
            {availableVarietals.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <input
            type="number"
            min="0"
            placeholder="Start (yrs)"
            value={newStart}
            onChange={(e) => setNewStart(e.target.value)}
            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
          />
          <input
            type="number"
            min="0"
            placeholder="End (yrs)"
            value={newEnd}
            onChange={(e) => setNewEnd(e.target.value)}
            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
          />
          <input
            type="text"
            placeholder="Notes (optional)"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            className="flex-1 min-w-[200px] border border-gray-300 rounded px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!newVarietalId || adding}
            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
          >
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
